import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CompressImageFile } from '../../components/compressFile/CompressImageFile';
import { useLocation, useNavigate } from 'react-router-dom';

const REQUEST_TYPES = [
     { value: 'missed_punch', label: 'Missed Punch' },
     { value: 'no_punch', label: 'No Punch' },
     { value: 'on_duty', label: 'On Duty' },
];

export default function RegularizeRequestForm() {
     const location = useLocation();
     const navigate = useNavigate();

     // Re-parse query params whenever location changes
     const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
     const queryDate = query.get('date');
     const editId = query.get('id');
     const isEdit = query.get('edit') === '1' && !!editId;
     const stateDate = location.state?.date;
     const [date, setDate] = useState(stateDate || queryDate || '');

     const [token, setToken] = useState(() => sessionStorage.getItem('token') || localStorage.getItem('token'));
     const BASE_URL = process.env.REACT_APP_BASE_URL;

     // Token validation - redirect to login if no token found
     useEffect(() => {
          const currentToken = sessionStorage.getItem('token') || localStorage.getItem('token');

          console.log('Token validation check:', {
               tokenExists: !!currentToken,
               tokenLength: currentToken?.length,
               isEdit,
               editId,
               pathname: location.pathname
          });

          if (!currentToken) {
               console.error('No authentication token found - Redirecting to login');
               navigate('/login', { replace: true, state: { from: location.pathname + location.search } });
          } else if (currentToken !== token) {
               // Token was found but state is stale - update it
               console.log('Updating token state');
               setToken(currentToken);
          }
     }, [location.pathname, location.search, isEdit, editId, token, navigate]); // Re-check when route or query/token/edit state changes

     const [inTime, setInTime] = useState('');
     const [outTime, setOutTime] = useState('');
     const [, setLoadingExisting] = useState(false);
     const [requestType, setRequestType] = useState(REQUEST_TYPES[0].value);
     const [reason, setReason] = useState('');
     const [, setFile] = useState(null);
     const [processedFile, setProcessedFile] = useState(null);
     const [filePreview, setFilePreview] = useState(null);
     const [fileLoading, setFileLoading] = useState(false);
     const [fileMessage, setFileMessage] = useState('');
     const [existingAttachments, setExistingAttachments] = useState([]);
     const createdObjectUrls = useRef(new Set());

     // Thumbnails for existing attachments (fetched via protected endpoint with Authorization)
     const [attachmentThumbs, setAttachmentThumbs] = useState({}); // { [attachmentId]: { url, contentType } }
     const thumbAbortControllers = useRef({});
     const isImageName = (name) => {
          if (!name) return false;
          const ext = (name.split('.').pop() || '').toLowerCase();
          return ['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext);
     };

     useEffect(() => {
          // Abort any previous thumbnail fetches
          Object.values(thumbAbortControllers.current || {}).forEach((ac) => { try { ac.abort(); } catch (e) { } });
          thumbAbortControllers.current = {};

          // Prefetch thumbnails for attachments that look like images
          existingAttachments.forEach((a) => {
               const id = a.id;
               if (!id) return;
               const maybeMime = (a.mime || a.content_type || '').toLowerCase();
               if (!(maybeMime.indexOf('image/') === 0 || isImageName(a.original_name))) return;
               if (attachmentThumbs[id]) return; // already fetched

               const ac = new AbortController();
               thumbAbortControllers.current[id] = ac;

               (async () => {
                    try {
                         const tokenLocal = sessionStorage.getItem('token') || localStorage.getItem('token') || sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || '';
                         const ap = a.file_path || a.path || '';
                         const url = (ap && ap.indexOf('uploads/regularize') !== -1) ? `${BASE_URL}/regularize/download/${encodeURIComponent(ap.split('/').pop())}` : `${BASE_URL}/${ap}`;
                         const headers = tokenLocal ? { Authorization: `Bearer ${tokenLocal}` } : {};
                         const res = await fetch(url, { method: 'GET', headers, signal: ac.signal });
                         if (process.env.NODE_ENV !== 'production') console.debug('Thumbnail fetch (form)', { url, ok: res.ok, status: res.status, contentType: res.headers.get('content-type') });
                         if (!res.ok) {
                              // mark as failed so spinner is not shown indefinitely
                              setAttachmentThumbs((prev) => ({ ...prev, [id]: { failed: true } }));
                              return;
                         }

                         // Prefer server content-type, but fall back to extension->mime map
                         let contentType = (res.headers.get('content-type') || '').toLowerCase();
                         const nm = a.original_name || a.file_path || '';
                         const ext = (nm.split('.').pop() || '').toLowerCase();
                         const extMap = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', svg: 'image/svg+xml' };

                         if (!contentType || !contentType.startsWith('image/')) {
                              if (ext && extMap[ext]) contentType = extMap[ext];
                              else {
                                   // Not an image (or unknown extension) - mark failed to avoid spinner
                                   setAttachmentThumbs((prev) => ({ ...prev, [id]: { failed: true } }));
                                   return;
                              }
                         }

                         const arrayBuffer = await res.arrayBuffer();
                         const blob = new Blob([arrayBuffer], { type: contentType });
                         const blobUrl = URL.createObjectURL(blob);
                         createdObjectUrls.current.add(blobUrl);
                         setAttachmentThumbs((prev) => ({ ...prev, [id]: { url: blobUrl, contentType } }));
                    } catch (e) {
                         if (e && e.name === 'AbortError') return;
                         console.warn('Thumbnail fetch failed for', a.file_path, e && e.message);
                         setAttachmentThumbs((prev) => ({ ...prev, [id]: { failed: true } }));
                    } finally {
                         try { delete thumbAbortControllers.current[id]; } catch (e) { }
                    }
               })();
          });

          return () => {
               // Abort any in-progress thumbnail requests on unmount/cleanup
               Object.values(thumbAbortControllers.current || {}).forEach((ac) => { try { ac.abort(); } catch (e) { } });
               thumbAbortControllers.current = {};
          };
     }, [existingAttachments, BASE_URL, attachmentThumbs]);

     const [viewerOpen, setViewerOpen] = useState(false);
     const [viewerUrl, setViewerUrl] = useState(null);
     const [viewerContentType, setViewerContentType] = useState('');
     const [viewerName, setViewerName] = useState('');

     const [submitting, setSubmitting] = useState(false);
     const [errors, setErrors] = useState({});

     const openBlobViewer = (blobUrl, contentType, name) => {
          setViewerUrl(blobUrl);
          setViewerContentType(contentType || '');
          setViewerName(name || 'Preview');
          setViewerOpen(true);
     };

     const closeViewer = () => {
          setViewerOpen(false);
          try {
               if (viewerUrl) {
                    URL.revokeObjectURL(viewerUrl);
                    createdObjectUrls.current.delete(viewerUrl);
               }
          } catch (e) { /* ignore */ }
          setViewerUrl(null);
          setViewerContentType('');
          setViewerName('');
     };
     const [isAllowed, setIsAllowed] = useState(true);
     const [tooLateMessage, setTooLateMessage] = useState('');


     useEffect(() => {
          // Determine whether requested date is between yesterday and 35 days ago (inclusive)
          const checkAllowed = () => {
               if (!date) {
                    setIsAllowed(false);
                    setTooLateMessage('No date selected for regularization.');
                    return;
               }
               const d = new Date(date);
               if (isNaN(d)) {
                    setIsAllowed(false);
                    setTooLateMessage('Invalid date provided.');
                    return;
               }
               const today = new Date();
               // normalize to midnight
               today.setHours(0, 0, 0, 0);
               d.setHours(0, 0, 0, 0);
               const diffMs = today - d;
               const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
               // Only allow dates that are at least 1 day old and at most 35 days ago.
               if (diffDays <= 0) {
                    setIsAllowed(false);
                    setTooLateMessage('Regularization is not allowed for this date.');
               } else if (diffDays > 35) {
                    setIsAllowed(false);
                    setTooLateMessage("It's too late to submit a regularization request for this date. Requests are accepted within 35 days of the attendance date.");
               } else {
                    setIsAllowed(true);
                    setTooLateMessage('');
               }
          };
          checkAllowed();
     }, [date]);

     // helper: convert several time formats to "HH:MM" for <input type="time">
     const toTimeInputValue = (time) => {
          if (!time) return '';
          try {
               let t = String(time).trim();
               // If full datetime: "YYYY-MM-DD HH:MM:SS" or ISO "YYYY-MM-DDTHH:MM:SSZ"
               if (t.includes(' ')) t = t.split(' ')[1];
               if (t.includes('T')) t = t.split('T')[1];
               // remove timezone suffix and seconds if present
               t = t.replace(/Z$/, '').replace(/\+.*$/, '');
               const parts = t.split(':');
               if (parts.length >= 2) {
                    const hh = parts[0].padStart(2, '0');
                    const mm = parts[1].padStart(2, '0');
                    return `${hh}:${mm}`;
               }
               return '';
          } catch {
               return '';
          }
     };

     // fetch the existing data for the date if needed (prefill punch / scheduled times)
     const fetchExistingData = React.useCallback(async () => {
          if (!date) {
               console.debug('fetchExistingData: no date');
               return;
          }
          const employee_code = sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || '';
          if (!employee_code) {
               console.debug('fetchExistingData: no employee_code');
               return;
          }
          setLoadingExisting(true);
          try {
               const res = await fetch(`${BASE_URL}/getEmployeeAttendanceOnDate`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ employee_code, date }),
               });
               if (!res.ok) {
                    console.warn('getEmployeeAttendanceOnDate fetch failed', res.status);
                    setLoadingExisting(false);
                    return;
               }
               const payload = await res.json();
               console.debug('getEmployeeAttendanceOnDate response', payload);
               // primary: payload.data.all_punches (API returns { status:true, data:{ all_punches: [...] }})
               const punches = (payload.data && Array.isArray(payload.data.all_punches) && payload.data.all_punches.length)
                    ? [...payload.data.all_punches].sort()
                    : (Array.isArray(payload.all_punches) ? [...payload.all_punches].sort() : []);

               if (punches.length) {
                    setInTime(toTimeInputValue(punches[0]));
                    setOutTime(toTimeInputValue(punches[punches.length - 1]));
               } else {
                    // fallback: older API shape (attendance / scheduled fields)
                    const att = payload.data?.attendance || payload.attendance || payload.data || payload;
                    if (att) {
                         if (att.punch_in) setInTime(toTimeInputValue(att.punch_in));
                         else if (att.scheduled_in) setInTime(toTimeInputValue(att.scheduled_in));

                         if (att.punch_out) setOutTime(toTimeInputValue(att.punch_out));
                         else if (att.scheduled_out) setOutTime(toTimeInputValue(att.scheduled_out));

                         if (att.notes && !reason) setReason(String(att.notes).slice(0, 500));
                    }
               }
          } catch (err) {
               console.error('Failed to fetch attendance for date', date, err);
          } finally {
               setLoadingExisting(false);
          }
     }, [BASE_URL, date, token, reason]);

     // fetch existing attendance once date is set and allowed
     // If edit mode is active, fetch the existing request details to prefill form
     useEffect(() => {
          if (isEdit && editId) {
               (async () => {
                    try {
                         console.log('Fetching existing request for edit:', { editId, tokenExists: !!token, tokenLength: token?.length });

                         const res = await fetch(`${BASE_URL}/regularize/${editId}`, {
                              headers: {
                                   Authorization: token ? `Bearer ${token}` : undefined
                              }
                         });

                         console.log('Edit fetch response:', { status: res.status, ok: res.ok });

                         if (!res.ok) {
                              const errorText = await res.text();
                              console.error('Failed to fetch request:', res.status, errorText);
                              throw new Error('Failed to fetch request');
                         }

                         const payload = await res.json();
                         console.log('Successfully fetched request data:', payload);

                         const data = payload.data || payload;
                         // prefill fields
                         setInTime(toTimeInputValue(data.punch_in));
                         setOutTime(toTimeInputValue(data.punch_out));
                         setRequestType(data.request_type || REQUEST_TYPES[0].value);
                         setReason(data.reason || '');
                         setFile(null);
                         setProcessedFile(null);
                         setFilePreview(null);
                         // set the date from the fetched request so the form knows the for_date when editing
                         const fetchedDate = data.for_date || data.forDate || data.date || '';
                         if (fetchedDate) setDate(fetchedDate);
                         // attachments
                         setExistingAttachments(payload.attachments || []);
                    } catch (e) {
                         console.error('Failed to load request for edit', e);
                         alert('Failed to load request for editing. Please try again.');
                         navigate('/attendance', { replace: true });
                    }
               })();
               return;
          }

          if (isAllowed && date) {
               fetchExistingData();
          }
     }, [date, isAllowed, isEdit, editId, token, fetchExistingData, BASE_URL, navigate]);



     const sanitizeFileName = (name) => {
          return name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
     };

     // Format date for display (e.g., "Jan 7, 2026"). Falls back to the raw value if parsing fails.
     const formatDisplayDate = (d) => {
          if (!d) return 'No date selected';
          try {
               const dt = new Date(d);
               if (isNaN(dt)) return String(d);
               return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
          } catch (e) {
               return String(d);
          }
     };

     // Download an uploaded attachment via protected endpoint using Authorization header
     const handleDownload = async (filePath, suggestedName) => {
          // Open a blank tab synchronously to avoid popup blockers when navigating later
          let blankWindow = null;
          try {
               blankWindow = window.open('', '_blank');
          } catch (e) {
               blankWindow = null;
          }

          try {
               // Try common storage keys used across the app for tokens
               const tokenLocal = sessionStorage.getItem('token') || localStorage.getItem('token') || sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || '';
               if (!filePath) throw new Error('No file specified');
               // if filePath looks like uploads/regularize/... use protected download endpoint
               const url = (typeof filePath === 'string' && filePath.indexOf('uploads/regularize') !== -1) ? `${BASE_URL}/regularize/download/${encodeURIComponent((filePath || '').split('/').pop())}` : `${BASE_URL}/${filePath}`;
               const headers = tokenLocal ? { Authorization: `Bearer ${tokenLocal}` } : {};

               console.debug('Downloading attachment', { url, tokenPresent: !!tokenLocal });

               const res = await fetch(url, { method: 'GET', headers });
               if (process.env.NODE_ENV !== 'production') console.debug('Attachment download response', { url, ok: res.ok, status: res.status, contentType: res.headers.get('content-type') });
               if (!res.ok) {
                    // Try to surface a helpful error message (JSON or text)
                    let bodyText = null;
                    try { bodyText = await res.text(); } catch (e) { bodyText = null; }
                    let msg = res.statusText || 'Failed to download file';
                    try {
                         const parsed = bodyText ? JSON.parse(bodyText) : null;
                         if (parsed && (parsed.message || parsed.error)) msg = parsed.message || parsed.error;
                         else if (bodyText) msg = bodyText;
                    } catch (e) {
                         if (bodyText) msg = bodyText;
                    }
                    console.warn('Attachment download failed', res.status, msg);
                    if (blankWindow) { blankWindow.close(); }
                    alert(`Download failed (${res.status}): ${msg}`);
                    return;
               }

               // Prefer the server Content-Type, but fall back to an extension->mime map when server returns generic/empty type
               let contentType = (res.headers.get('content-type') || '').toLowerCase();
               const nm = suggestedName || filePath.split('/').pop() || '';
               const ext = (nm.split('.').pop() || '').toLowerCase();
               const extMap = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', svg: 'image/svg+xml' };
               if (!contentType || contentType === 'application/octet-stream') {
                    if (ext && extMap[ext]) contentType = extMap[ext];
               }

               const arrayBuffer = await res.arrayBuffer();
               const blob = new Blob([arrayBuffer], { type: contentType || 'application/octet-stream' });
               const blobUrl = URL.createObjectURL(blob);
               createdObjectUrls.current.add(blobUrl);

               const isImage = contentType.indexOf('image/') === 0 || ['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext);
               const isPdf = contentType.indexOf('application/pdf') === 0 || ext === 'pdf';

               // If it's an image or PDF, prefer opening in a new tab; if blankWindow is available use it
               if (isImage || isPdf) {
                    if (blankWindow) {
                         try {
                              // Write a small HTML wrapper so the resource renders correctly (image/pdf) instead of showing raw bytes
                              if (isImage) {
                                   blankWindow.document.open();
                                   blankWindow.document.write("<html><head><meta charset=\"utf-8\"><title>" + (suggestedName || '') + "</title></head><body style=\"margin:0;display:flex;align-items:center;justify-content:center;background:#111;\">" +
                                        "<img src=\"" + blobUrl + "\" alt=\"" + (suggestedName || '') + "\" style=\"max-width:100%;height:auto;\">" +
                                        "</body></html>");
                                   blankWindow.document.close();
                                   return;
                              } else {
                                   // PDF: embed in an iframe for consistent viewing
                                   blankWindow.document.open();
                                   blankWindow.document.write("<html><head><meta charset=\"utf-8\"><title>" + (suggestedName || '') + "</title></head><body style=\"margin:0;height:100vh;\">" +
                                        "<iframe src=\"" + blobUrl + "\" style=\"width:100%;height:100%;border:0;\"></iframe>" +
                                        "</body></html>");
                                   blankWindow.document.close();
                                   return;
                              }
                         } catch (e) {
                              // if writing fails (rare), close and fall back to modal/download
                              try { blankWindow.close(); } catch (e2) { }
                              blankWindow = null;
                         }
                    }

                    // If we couldn't open a new tab, fallback: images -> modal, PDFs -> modal iframe
                    if (isImage) {
                         openBlobViewer(blobUrl, contentType || 'image/*', suggestedName);
                         return;
                    }
                    if (isPdf) {
                         openBlobViewer(blobUrl, contentType || 'application/pdf', suggestedName);
                         return;
                    }
               }

               // Fallback: download via anchor
               const a = document.createElement('a');
               a.href = blobUrl;
               a.download = suggestedName || nm;
               document.body.appendChild(a);
               a.click();
               a.remove();
          } catch (err) {
               console.error('Attachment download failed', err);
               if (blankWindow) { try { blankWindow.close(); } catch (e) { } }
               alert('Failed to download attachment: ' + (err && err.message ? err.message : err));
          }
     };

     useEffect(() => {
          const snapshot = Array.from(createdObjectUrls.current);
          return () => {
               snapshot.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) { } });
          };
     }, []);

     const validate = () => {
          const err = {};

          if (!inTime) err.inTime = 'Please select In Time.';
          if (!outTime) err.outTime = 'Please select Out Time.';

          if (inTime && outTime) {
               const [ih, im] = inTime.split(':').map(Number);
               const [oh, om] = outTime.split(':').map(Number);
               if (ih * 60 + im >= oh * 60 + om) err.time = 'Out time should be later than In time.';
          }

          if (!requestType) err.requestType = 'Please select a request type.';

          // require processed file unless editing and an existing attachment is present
          if (!processedFile && !(isEdit && existingAttachments && existingAttachments.length > 0)) {
               err.file = 'Please attach a supporting document.';
          }

          if (!reason || reason.trim().length < 5) err.reason = 'Please provide a reason (min 5 characters).';

          setErrors(err);
          return Object.keys(err).length === 0;
     };

     // Replace with robust file processing: compress images and validate PDFs
     const handleFileInput = async (files) => {
          const file = files && files[0];
          if (!file) return;

          setFileLoading(true);
          setFileMessage('Processing...');
          setProcessedFile(null);
          setFilePreview(null);

          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
          const MAX_SIZE_MB = 2;

          try {
               const fileType = file.type;
               let fileSize = file.size / 1024 / 1024;

               if (!validTypes.includes(fileType)) {
                    setFile(null);
                    setFileMessage('Only JPG, PNG or PDF allowed');
                    setFileLoading(false);
                    return;
               }

               if (fileType === 'application/pdf') {
                    if (fileSize > MAX_SIZE_MB) {
                         document.getElementById('regularize_attachment').value = '';
                         setFile(null);
                         setFileMessage(`PDF exceeds ${MAX_SIZE_MB}MB`);
                         setFileLoading(false);
                         return;
                    }

                    const arrayBuffer = await file.arrayBuffer();
                    const uint8 = new Uint8Array(arrayBuffer);
                    const header = [0x25, 0x50, 0x44, 0x46]; // %PDF
                    const isPdf = header.every((b, idx) => uint8[idx] === b);
                    if (!isPdf) {
                         setFile(null);
                         setFileMessage('Invalid or corrupted PDF');
                         setFileLoading(false);
                         return;
                    }

                    // Detect embedded scripts / JavaScript inside PDF (scan entire file)
                    try {
                         // decode as latin1 to keep byte-to-char mapping (no UTF-8 errors)
                         const fullText = new TextDecoder('latin1', { fatal: false }).decode(uint8);

                         // quick check across entire file
                         const scriptPattern = /(\/JavaScript|\/JS|\/OpenAction|\/AA|\/Launch|\/RichMedia|\/EmbeddedFile|\/AcroForm)/i;
                         if (scriptPattern.test(fullText)) {
                              setFile(null);
                              setFileMessage('PDF contains embedded scripts and is not allowed');
                              setFileLoading(false);
                              return;
                         }

                         // Inspect object streams: look for stream..endstream and try to detect scripts inside.
                         const streamRegex = /stream\s*([\s\S]*?)\s*endstream/gi;
                         let m;
                         let pako = null;
                         while ((m = streamRegex.exec(fullText)) !== null) {
                              const streamContent = m[1];
                              const before = fullText.slice(Math.max(0, m.index - 200), m.index);

                              // if dictionary mentions FlateDecode, try decompressing
                              if (/FlateDecode/i.test(before)) {
                                   try {
                                        if (!pako) {
                                             // dynamic import - optional; if unavailable we fallback to server-side detection
                                             pako = await import('pako');
                                             pako = pako.default || pako;
                                        }
                                        // convert streamContent (latin1) back to bytes
                                        const len = streamContent.length;
                                        const streamBuf = new Uint8Array(len);
                                        for (let i = 0; i < len; i++) streamBuf[i] = streamContent.charCodeAt(i) & 0xff;

                                        // Try to inflate (may throw)
                                        let inflated = null;
                                        try {
                                             inflated = pako.inflate(streamBuf);
                                        } catch (e1) {
                                             try { inflated = pako.ungzip ? pako.ungzip(streamBuf) : null; } catch (e2) { inflated = null; }
                                        }

                                        if (inflated) {
                                             const txt = new TextDecoder('utf-8', { fatal: false }).decode(inflated);
                                             if (/(javascript|\/javascript|openaction|launch|acroform|embeddedfile|richmedia)/i.test(txt)) {
                                                  setFile(null);
                                                  setFileMessage('PDF contains embedded scripts and is not allowed');
                                                  setFileLoading(false);
                                                  return;
                                             }
                                        }
                                   } catch (e) {
                                        // dynamic import could fail in some builds - ignore and rely on server-side
                                        console.warn('Could not decompress PDF stream client-side (continuing):', e && e.message);
                                   }
                              } else {
                                   // plain text streams: check for script tokens
                                   if (/(javascript|\/javascript|openaction|launch|acroform|embeddedfile|richmedia)/i.test(streamContent)) {
                                        setFile(null);
                                        setFileMessage('PDF contains embedded scripts and is not allowed');
                                        setFileLoading(false);
                                        return;
                                   }
                              }
                         }

                    } catch (e) {
                         // ignore decode errors and continue — server will perform deeper checks
                         console.warn('PDF scanning failed client-side, letting server validate:', e && e.message);
                    }

                    const safeName = `${Date.now()}_${sanitizeFileName(file.name)}`;
                    const finalFile = new File([file], safeName, { type: file.type });
                    const pdfIcon = 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#dc3545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><text x="12" y="18" font-family="Arial, sans-serif" font-size="4" text-anchor="middle" fill="#dc3545">PDF</text></svg>`);

                    setProcessedFile(finalFile);
                    setFile(finalFile);
                    setFilePreview(pdfIcon);
                    setFileMessage('PDF processed successfully');
                    setFileLoading(false);
                    return;
               }

               // Image handling
               let uploadFile = file;
               let compressionAttempts = 0;
               const MAX_COMPRESSION_ATTEMPTS = 3;

               if (fileSize > MAX_SIZE_MB) {
                    while (fileSize > MAX_SIZE_MB && compressionAttempts < MAX_COMPRESSION_ATTEMPTS) {
                         compressionAttempts++;
                         setFileMessage(`Compressing... (${compressionAttempts}/${MAX_COMPRESSION_ATTEMPTS})`);
                         const targetSize = MAX_SIZE_MB - (0.2 * compressionAttempts);
                         try {
                              uploadFile = await CompressImageFile(uploadFile, targetSize, `REG_attempt${compressionAttempts}`);
                              fileSize = uploadFile.size / 1024 / 1024;
                              // continue
                         } catch (compressionError) {
                              console.error('Compression failed:', compressionError);
                              break;
                         }
                    }

                    if (fileSize > MAX_SIZE_MB) {
                         document.getElementById('regularize_attachment').value = '';
                         setFile(null);
                         setFileMessage(`Image too large after compression (${fileSize.toFixed(1)}MB)`);
                         setFileLoading(false);
                         return;
                    }
               }

               const safeName = `${Date.now()}_${sanitizeFileName(uploadFile.name)}`;
               const processed = new File([uploadFile], safeName, { type: uploadFile.type });

               // cleanup previous preview if any
               try {
                    if (filePreview && createdObjectUrls.current.has(filePreview)) {
                         URL.revokeObjectURL(filePreview);
                         createdObjectUrls.current.delete(filePreview);
                    }
               } catch (e) { /* ignore */ }

               const previewUrl = URL.createObjectURL(processed);
               createdObjectUrls.current.add(previewUrl);

               setProcessedFile(processed);
               setFile(processed);
               setFilePreview(previewUrl);
               setFileMessage(compressionAttempts > 0 ? `Compressed successfully (${fileSize.toFixed(1)}MB)` : 'Image processed successfully');
               setFileLoading(false);

          } catch (err) {
               console.error('Error processing file:', err);
               setFile(null);
               setProcessedFile(null);
               setFilePreview(null);
               setFileMessage(`Failed to process file: ${err.message || 'Unknown error'}`);
               setFileLoading(false);
          }
     };

     const handleSubmit = async (e) => {
          e.preventDefault();
          if (!isAllowed) {
               alert(tooLateMessage || 'Regularization not allowed for this date.');
               return;
          }
          if (!validate()) return;
          setSubmitting(true);
          try {
               // Build payload and send to backend regularize endpoint
               // backend expects emp_code, for_date, punch_in, punch_out, reason (POST JSON or multipart)
               const employee_code = sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || '';
               if (!employee_code) {
                    throw new Error('Employee code not found in session storage');
               }
               let res;
               // Use multipart/form-data so we can always include the processed attachment
               // New structure: send `data` (JSON) and `files[]` entries
               const payloadData = {
                    emp_code: employee_code,
                    for_date: date,
                    punch_in: inTime || null,
                    punch_out: outTime || null,
                    reason,
                    request_type: requestType
               };

               const fd = new FormData();
               fd.append('data', JSON.stringify(payloadData));
               // append one or more files as files[0], files[1], ...
               if (processedFile) fd.append('files[0]', processedFile, processedFile.name);

               // If editing, send to update endpoint
               const targetUrl = isEdit && editId ? `${BASE_URL}/regularize/${editId}/update` : `${BASE_URL}/regularize/apply`;

               // Debug: log FormData contents (for dev only)
               try {
                    for (const pair of fd.entries()) {
                         console.log('FormData entry:', pair[0], pair[1]);
                    }
               } catch (e) { console.warn('Could not enumerate FormData entries', e); }

               // Only include Authorization header if token exists (avoid undefined header values)
               const headers = {};
               if (token) headers.Authorization = `Bearer ${token}`;

               // Do not send credentials by default unless required
               res = await fetch(targetUrl, {
                    method: 'POST',
                    body: fd,
                    headers
               });

               if (!res.ok) {
                    // Try to read JSON, otherwise fall back to text for better diagnostics
                    const text = await res.text().catch(() => null);
                    let errMsg = 'Submit failed';
                    try {
                         const parsed = text ? JSON.parse(text) : null;
                         errMsg = parsed?.message || parsed?.error || text || errMsg;
                    } catch (e) {
                         errMsg = text || errMsg;
                    }
                    console.error('Server response error:', res.status, errMsg);
                    throw new Error(errMsg);
               }

               // success
               navigate('/attendance', { replace: true, state: { showMessage: 'Regularization request submitted' } });
          } catch (err) {
               console.error(err);
               alert(err.message || 'Failed to submit request. Please try again.');
          } finally {
               setSubmitting(false);
          }
     };

     const [checkingExisting, setCheckingExisting] = useState(false);
     const [existingRequestChecked, setExistingRequestChecked] = useState(false);

     // Check for existing request when date and employee code are available
     useEffect(() => {
          console.log('useEffect triggered for checkForExistingRequest');
          console.log('Dependencies:', { date, isAllowed, isEdit, existingRequestChecked, navigate, token, BASE_URL });

          const checkForExistingRequest = async () => {
               console.log('checkForExistingRequest function called');
               console.log('Debug check:', {
                    date,
                    isEdit,
                    isAllowed,
                    existingRequestChecked,
                    employee_code: sessionStorage.getItem('user_code'),
                    BASE_URL,
                    token: !!token
               });

               if (!date) {
                    console.log('Exiting: no date');
                    return;
               }
               if (isEdit) {
                    console.log('Exiting: isEdit is true');
                    return;
               }
               if (!isAllowed) {
                    console.log('Exiting: isAllowed is false');
                    return;
               }
               if (existingRequestChecked) {
                    console.log('Exiting: existingRequestChecked is true');
                    return;
               }

               const employee_code = sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || '';
               if (!employee_code) {
                    console.log('Exiting: no employee_code');
                    return;
               }

               console.log('All conditions passed, proceeding with API call');
               setCheckingExisting(true);
               try {
                    const url = `${BASE_URL}/regularize/check-existing?emp_code=${encodeURIComponent(employee_code)}&for_date=${encodeURIComponent(date)}`;
                    console.log('Fetching URL:', url);

                    const res = await fetch(url, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: token ? `Bearer ${token}` : undefined,
                         },
                    });

                    console.log('Response received:', { status: res.status, ok: res.ok });

                    if (!res.ok) {
                         console.warn('Failed to check existing request', res.status);
                         const text = await res.text();
                         console.warn('Response text:', text);
                         setCheckingExisting(false);
                         setExistingRequestChecked(true);
                         return;
                    }

                    const payload = await res.json();
                    console.log('Existing request check response:', payload);

                    if (payload.exists && payload.data) {
                         const existingId = payload.data.id;
                         const status = (payload.data.status || '').toLowerCase();

                         if (status === 'pending') {
                              console.log('Redirecting to edit page for pending request');
                              console.log('Current token before redirect:', sessionStorage.getItem('token') ? 'EXISTS' : 'MISSING');
                              console.log('Token length:', sessionStorage.getItem('token')?.length);

                              // Use app-registered route `/regularize-request` (not `/regularize/request`)
                              navigate(`/regularize-request?date=${encodeURIComponent(date)}&id=${existingId}&edit=1`, {
                                   replace: true,
                                   state: { date, existingRequest: payload.data }
                              });
                         } else {
                              console.log('Request exists but status is not pending:', status);
                              alert(`A regularization request for ${formatDisplayDate(date)} already exists with status: ${payload.data.status}. You cannot modify it.`);
                              navigate('/attendance', { replace: true });
                         }
                    } else {
                         console.log('No existing request found, proceeding with new request');
                         setExistingRequestChecked(true);
                    }
               } catch (err) {
                    console.error('Failed to check existing request', err);
                    setExistingRequestChecked(true);
               } finally {
                    setCheckingExisting(false);
                    console.log('checkForExistingRequest completed');
               }
          };

          checkForExistingRequest();
     }, [date, isAllowed, isEdit, existingRequestChecked, navigate, token, BASE_URL]);

     return (
          <div className="container mt-4">
               <div className="card shadow-sm">
                    <div className="card-body">
                         {checkingExisting && (
                              <div className="alert alert-info d-flex align-items-center" role="alert">
                                   <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                   <span>Checking for existing requests...</span>
                              </div>
                         )}

                         {!checkingExisting && (
                              <>
                                   <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                             <h5 className="mb-0">Regularization Request</h5>
                                             <div className="small text-muted">Request for</div>
                                        </div>
                                        <div className="text-end">
                                             <div className="small text-muted">Date</div>
                                             <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>{formatDisplayDate(date)}</div>
                                        </div>
                                   </div>

                                   {!isAllowed && (
                                        <div className="alert alert-danger" role="alert">
                                             {tooLateMessage}
                                        </div>
                                   )}

                                   <form onSubmit={handleSubmit} noValidate>
                                        <div className="row g-3">
                                             <div className="col-md-6">
                                                  <label className="form-label">In Time <span className="text-danger">*</span></label>
                                                  <input
                                                       type="time"
                                                       className={`form-control ${errors.inTime ? 'is-invalid' : ''}`}
                                                       value={inTime}
                                                       onChange={(e) => setInTime(e.target.value)}
                                                       disabled={!isAllowed || submitting}
                                                  />
                                                  {errors.inTime && <div className="invalid-feedback">{errors.inTime}</div>}
                                             </div>

                                             <div className="col-md-6">
                                                  <label className="form-label">Out Time <span className="text-danger">*</span></label>
                                                  <input
                                                       type="time"
                                                       className={`form-control ${errors.outTime ? 'is-invalid' : ''}`}
                                                       value={outTime}
                                                       onChange={(e) => setOutTime(e.target.value)}
                                                       disabled={!isAllowed || submitting}
                                                  />
                                                  {errors.outTime && <div className="invalid-feedback">{errors.outTime}</div>}
                                             </div>

                                             <div className="col-md-6">
                                                  <label className="form-label">Request Type <span className="text-danger">*</span></label>
                                                  <select
                                                       className={`form-select ${errors.requestType ? 'is-invalid' : ''}`}
                                                       value={requestType}
                                                       onChange={(e) => setRequestType(e.target.value)}
                                                       disabled={!isAllowed || submitting}
                                                  >
                                                       {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                  </select>
                                                  {errors.requestType && <div className="invalid-feedback">{errors.requestType}</div>}
                                             </div>

                                             <div className="col-md-6">
                                                  <label className="form-label">Attachment {(!isEdit || !existingAttachments || existingAttachments.length === 0) && <span className="text-danger">*</span>}</label>
                                                  <input
                                                       type="file"
                                                       className={`form-control ${errors.file || fileMessage ? 'is-invalid' : ''}`}
                                                       id="regularize_attachment"
                                                       onChange={async (e) => await handleFileInput(e.target.files)}
                                                       accept=".png,.jpg,.jpeg,.pdf"
                                                       disabled={!isAllowed || submitting}
                                                  />
                                                  {/* Helper: when editing and an existing attachment exists, explain behavior */}
                                                  {isEdit && existingAttachments && existingAttachments.length > 0 && !fileLoading && (
                                                       <div className="form-text small text-muted mt-1">Leave blank to keep existing attachment; upload a file to replace it.</div>
                                                  )}
                                                  {errors.file && <div className="invalid-feedback">{errors.file}</div>}
                                                  {!errors.file && fileMessage && !fileLoading && <div className="small text-danger mt-1">{fileMessage}</div>}
                                                  {fileLoading && <div className="small text-muted mt-1"><span className="spinner-border spinner-border-sm me-1"></span>{fileMessage || 'Processing...'}</div>}

                                                  {filePreview && (
                                                       <div className="mt-2">
                                                            {processedFile?.type === 'application/pdf' ? (
                                                                 <div style={{ width: 36, height: 36, border: '1px solid #dc3545', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                      <img src={filePreview} alt="pdf" style={{ width: 32, height: 32 }} />
                                                                 </div>
                                                            ) : (
                                                                 <img src={filePreview} alt="preview" style={{ maxWidth: 120, maxHeight: 90, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                                                            )}
                                                            <div className="small text-success">{!fileLoading && fileMessage}</div>
                                                       </div>
                                                  )}
                                             </div>

                                             <div className="col-12">
                                                  <label className="form-label">Reason <span className="text-danger">*</span></label>
                                                  <textarea
                                                       className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                                                       rows={4}
                                                       value={reason}
                                                       onChange={(e) => setReason(e.target.value)}
                                                       disabled={!isAllowed || submitting}
                                                  />
                                                  {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}
                                                  {/* Show existing attachments in edit mode */}
                                                  {isEdit && existingAttachments.length > 0 && (
                                                       <div className="mt-2">
                                                            <label className="form-label">Existing Attachments</label>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                 {existingAttachments.map((a) => (
                                                                      <div key={a.id} className="p-2 border rounded" style={{ fontSize: '0.85rem', minWidth: 220 }}>
                                                                           <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                                                {/* Thumbnail area */}
                                                                                {attachmentThumbs[a.id] && attachmentThumbs[a.id].url ? (
                                                                                     <img src={attachmentThumbs[a.id].url} alt={a.original_name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                                                                                ) : (isImageName(a.original_name) ? (
                                                                                     (attachmentThumbs[a.id] && attachmentThumbs[a.id].failed) ? (
                                                                                          <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                                                                                               <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><polyline points="14,2 14,8 20,8" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                                          </div>
                                                                                     ) : (
                                                                                          <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 4 }}>
                                                                                               <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                                          </div>
                                                                                     )
                                                                                ) : (
                                                                                     <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                                                                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><polyline points="14,2 14,8 20,8" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                                     </div>
                                                                                ))}

                                                                                <div style={{ flex: 1 }}>
                                                                                     <div style={{ fontWeight: 500 }}>{a.original_name}</div>
                                                                                     <div className="text-muted small">{(a.size / 1024).toFixed(1)} KB</div>
                                                                                     <div>
                                                                                          <button type="button" onClick={async () => { await handleDownload(a.file_path, a.original_name); }} className="btn btn-link link-primary p-0">View</button>
                                                                                     </div>
                                                                                </div>
                                                                           </div>
                                                                      </div>
                                                                 ))}
                                                            </div>
                                                       </div>
                                                  )}

                                                  {/* Modal viewer for images/PDFs */}
                                                  {viewerOpen && (
                                                       <div className="modal show d-block" tabIndex={-1} role="dialog" onClick={closeViewer}>
                                                            <div className="modal-dialog modal-xl modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
                                                                 <div className="modal-content">
                                                                      <div className="modal-header">
                                                                           <h5 className="modal-title">{viewerName}</h5>
                                                                           <button type="button" className="btn-close" onClick={closeViewer}></button>
                                                                      </div>
                                                                      <div className="modal-body" style={{ minHeight: 300 }}>
                                                                           {viewerContentType.startsWith('image/') ? (
                                                                                <div style={{ textAlign: 'center', background: '#111' }}>
                                                                                     <img src={viewerUrl} alt={viewerName} style={{ maxWidth: '100%', height: 'auto' }} />
                                                                                </div>
                                                                           ) : (
                                                                                <iframe src={viewerUrl} title={viewerName} style={{ width: '100%', height: '70vh', border: 0 }} />
                                                                           )}
                                                                      </div>
                                                                      <div className="modal-footer">
                                                                           <button className="btn btn-secondary" onClick={closeViewer}>Close</button>
                                                                           <a className="btn btn-primary" href={viewerUrl} download={viewerName}>Download</a>
                                                                      </div>
                                                                 </div>
                                                            </div>
                                                       </div>
                                                  )}                                   </div>

                                             {errors.time && <div className="col-12"><div className="text-danger small">{errors.time}</div></div>}

                                             <div className="col-12 d-flex gap-2 justify-content-end">
                                                  <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)} disabled={submitting}>Cancel</button>
                                                  <button type="submit" className="btn btn-primary" disabled={submitting || !isAllowed}>
                                                       {submitting ? (isEdit ? 'Updating…' : 'Submitting…') : (isEdit ? 'Update Request' : 'Submit Request')}
                                                  </button>
                                             </div>
                                        </div>
                                   </form>
                              </>
                         )}
                    </div>
               </div>
          </div>
     );
}